import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import type { Article } from "@/data/blogContent";

interface ArticleCardProps {
  article: Article;
  featured?: boolean;
  index?: number;
}

const imageModules = import.meta.glob("@/assets/blog/*.webp", { eager: true, import: "default" }) as Record<string, string>;

const ArticleCard = ({ article, featured = false, index = 0 }: ArticleCardProps) => {
  const articleImage = article.image ? imageModules[`/src/assets/${article.image}`] : undefined;

  if (featured) {
    return (
      <motion.article
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="md:grid md:grid-cols-2">
          <div className="h-56 md:h-full relative overflow-hidden">
            {articleImage ? (
              <img src={articleImage} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 gradient-hero" />
            )}
            <div className="absolute inset-0 gradient-hero opacity-40" />
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-body font-semibold">À la une</span>
            </div>
          </div>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-body font-semibold">{article.category}</span>
              <span className="flex items-center gap-1 text-muted-foreground text-xs font-body">
                <Clock size={12} /> {article.readTime}
              </span>
            </div>
            <Link to={`/blog/${article.slug}`}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3 hover:text-primary transition-colors">
                {article.title}
              </h2>
            </Link>
            <p className="font-body text-foreground/60 leading-relaxed line-clamp-3 mb-4">{article.excerpt}</p>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-muted-foreground text-xs font-body">
                <Calendar size={12} /> {article.publishedAt}
              </span>
              <Link to={`/blog/${article.slug}`} className="inline-flex items-center gap-1 font-body text-primary font-semibold text-sm hover:text-secondary transition-colors">
                Lire l'article <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow group flex flex-col"
    >
      <div className="h-1 bg-primary" />
      <div className="h-44 relative overflow-hidden">
        {articleImage ? (
          <img src={articleImage} alt={article.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 gradient-hero" />
        )}
        <div className="absolute inset-0 gradient-hero opacity-40 group-hover:opacity-30 transition-opacity" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-body font-semibold whitespace-nowrap">{article.category}</span>
          <span className="flex items-center gap-1 text-muted-foreground text-xs font-body whitespace-nowrap">
            <Calendar size={12} /> {article.publishedAt}
          </span>
          <span className="flex items-center gap-1 text-muted-foreground text-xs font-body whitespace-nowrap">
            <Clock size={12} /> {article.readTime}
          </span>
        </div>
        <Link to={`/blog/${article.slug}`}>
          <h3 className="font-heading text-xl font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {article.title}
          </h3>
        </Link>
        <p className="font-body text-foreground/60 text-sm leading-relaxed line-clamp-2 mb-4">{article.excerpt}</p>
        <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
          <span className="text-xs font-body text-muted-foreground">{article.author}</span>
          <Link to={`/blog/${article.slug}`} className="inline-flex items-center gap-1 font-body text-primary font-semibold text-sm hover:text-secondary transition-colors">
            Lire <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default ArticleCard;
